/** A `[start, end)` slice of a title that matched a query term. */
export interface HighlightRange {
  readonly start: number;
  readonly end: number;
}

/**
 * Where a result's title or subtitle matched the query (需求 FR-14 §4.3 关键词高亮).
 *
 * Recomputed at render time rather than returned by the matchers: `queryCommands`
 * answers "does this match", which is what filtering needs, and threading
 * positions through every provider would make each one responsible for a purely
 * presentational concern. Case-insensitive, and overlapping or adjacent hits are
 * merged so the face can paint one background per run.
 */
export function highlightRanges(
  text: string,
  terms: readonly string[],
): HighlightRange[] {
  const haystack = text.toLowerCase();
  const found: HighlightRange[] = [];

  for (const term of terms) {
    if (term.length === 0) {
      continue;
    }
    let from = 0;
    for (;;) {
      const at = haystack.indexOf(term, from);
      if (at === -1) {
        break;
      }
      found.push({ start: at, end: at + term.length });
      from = at + term.length;
    }
  }

  if (found.length === 0) {
    return [];
  }

  found.sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: HighlightRange[] = [found[0]];
  for (const range of found.slice(1)) {
    const last = merged[merged.length - 1];
    if (range.start <= last.end) {
      if (range.end > last.end) {
        merged[merged.length - 1] = { start: last.start, end: range.end };
      }
      continue;
    }
    merged.push(range);
  }
  return merged;
}

/** `text` split into alternating plain / highlighted pieces, in order. */
export interface HighlightSegment {
  readonly text: string;
  readonly match: boolean;
}

/** Split `text` for rendering; a single non-matching segment when nothing hit. */
export function highlightSegments(
  text: string,
  terms: readonly string[],
): HighlightSegment[] {
  const ranges = highlightRanges(text, terms);
  if (ranges.length === 0) {
    return [{ text, match: false }];
  }
  const segments: HighlightSegment[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) {
      segments.push({ text: text.slice(cursor, range.start), match: false });
    }
    segments.push({ text: text.slice(range.start, range.end), match: true });
    cursor = range.end;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), match: false });
  }
  return segments;
}
